const STORAGE_KEY = 'hraAnswerPreviewLanguage';

export function getStoredHraPreviewLanguage(fallback = 'en') {
    try {
        return window.localStorage.getItem(STORAGE_KEY) || fallback;
    } catch {
        return fallback;
    }
}

export function setStoredHraPreviewLanguage(language) {
    try {
        if (language) {
            window.localStorage.setItem(STORAGE_KEY, language);
        }
    } catch {
        // ignore quota / private mode
    }
}

export function resolveHraPreviewLanguage(
    availableLanguages = ['en'],
    fallback = 'en'
) {
    if (!availableLanguages?.length) {
        return fallback;
    }
    // Sole option must win — avoids a stale localStorage value flipping the
    // selection while the option list briefly includes more languages.
    if (availableLanguages.length === 1) {
        return availableLanguages[0];
    }
    const stored = getStoredHraPreviewLanguage(fallback);
    if (availableLanguages.includes(stored)) {
        return stored;
    }
    if (availableLanguages.includes(fallback)) {
        return fallback;
    }
    return availableLanguages[0] || 'en';
}

/** Limit preview language options to company-enabled languages when configured. */
export function filterPreviewLanguages(
    contentLanguages = [],
    enabledLanguages = [],
    fallback = 'en'
) {
    const languages =
        Array.isArray(contentLanguages) && contentLanguages.length
            ? contentLanguages
            : [fallback];
    if (!enabledLanguages?.length) {
        return languages;
    }
    const filtered = languages.filter((code) =>
        enabledLanguages.includes(code)
    );
    return filtered.length ? filtered : languages;
}

/**
 * Build the preview language option list for a company (or partner) context.
 * Returns [] until company languages are loaded so callers can defer fetching.
 */
export function getPreviewLanguageOptions({
    contentLanguages = [],
    enabledLanguages = [],
    companyLanguages = null,
    companyId = null,
    fallback = 'en',
} = {}) {
    if (companyId && !companyLanguages?.length) {
        return [];
    }
    const enabled = companyId ? enabledLanguages : [];
    return filterPreviewLanguages(contentLanguages, enabled, fallback);
}

/** Apply preview language to i18n (and optionally persist + reload overrides). */
export async function applyHraPreviewLanguage(
    i18n,
    languageKey,
    { persist = true, refreshOverrides = false } = {}
) {
    if (persist) {
        setStoredHraPreviewLanguage(languageKey);
    }
    await i18n.changeLanguage(languageKey);
    if (refreshOverrides) {
        const { refreshI18nPreview } = await import('./refreshI18nPreview');
        await refreshI18nPreview();
    }
}
